import type { ReactNode } from "react";

import type { createToaster } from "@ark-ui/react/toast";
import type { Options } from "@zag-js/toast";

type CreateToasterReturn = ReturnType<typeof createToaster<ReactNode>>;

type FlashType = "info" | "success" | "warning" | "danger" | "loading";

const DEFAULT_DURATION = 3000;

export type FlashOptions = Omit<
  Options<ReactNode>,
  "title" | "description" | "actions" | "type"
> & {
  icon?: false;
};

export type FlashApi = {
  success(title: string, options?: FlashOptions): string;
  danger(title: string, options?: FlashOptions): string;
  warning(title: string, options?: FlashOptions): string;
  info(title: string, options?: FlashOptions): string;
  loading(title: string, options?: FlashOptions): string;
  create(title: string, options?: FlashOptions & { type?: FlashType }): string;
} & Omit<
  CreateToasterReturn,
  "success" | "error" | "create" | "warning" | "info" | "loading"
>;

function buildFlash(
  toast: CreateToasterReturn,
  type: FlashType,
  title: string,
  options: FlashOptions = {},
): string {
  const { icon, meta, duration, ...rest } = options;
  return toast.create({
    ...rest,
    title,
    // Loading flashes stay on screen until dismissed or updated.
    duration: duration ?? (type === "loading" ? Infinity : DEFAULT_DURATION),
    type: type === "warning" || type === "danger" ? "info" : type,
    meta: {
      ...meta,
      variant: "flash",
      palette: type === "loading" ? "info" : type,
      type,
      icon,
    },
  });
}

export function createFlashApi(toast: CreateToasterReturn): FlashApi {
  return {
    ...toast,
    success(title, options) {
      return buildFlash(toast, "success", title, options);
    },
    danger(title, options) {
      return buildFlash(toast, "danger", title, options);
    },
    warning(title, options) {
      return buildFlash(toast, "warning", title, options);
    },
    info(title, options) {
      return buildFlash(toast, "info", title, options);
    },
    loading(title, options) {
      return buildFlash(toast, "loading", title, options);
    },
    create(title, options) {
      const { type = "info", ...rest } = options ?? {};
      return buildFlash(toast, type, title, rest);
    },
  };
}
